import React, { useContext, useMemo } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import styled from 'styled-components';
import { Navigate, useParams, Outlet } from 'react-router-dom';

import CategoryFilters from './CategoryFilters';

import {
  allowMarketplace,
  selectDefaultWallet,
  getActiveEmployeeWallets,
} from 'utils/employee';
import {
  filterActiveAlegeusPretaxAccountsWithFsaType,
  filterActiveFormaPretaxAccounts,
} from 'utils/pretaxAccount';
import { AccountRoutes } from 'utils/routeGenerator';

import CurrentUserContext from 'contexts/CurrentUserContext';
import PretaxContext from 'contexts/PretaxContext';

import useQueryCategoriesWithProducts from 'hooks/useQueryCategoriesWithProducts';

import Paper from '@joinforma/ui/components/Paper';
import { SCREEN_WIDTHS } from '@joinforma/ui/constants/breakpoints';

import { FSA_STORE_PRETAX_ACCOUNT_TYPES } from 'constants/accounts';

import { Employee, Company, PretaxAccount } from './types';
import FeatureFlagContext from 'contexts/FeatureFlagContext';
import { FEATURE_FLAGS } from 'constants/featureFlag';
import { PretaxWallet } from 'contexts/PretaxContext/types';

const Container = styled(Paper)`
  padding-top: 55px;
  display: flex;
  gap: 40px;
  height: 100%;
  max-width: 1102px;
  margin: 0 auto;
  @media (max-width: ${SCREEN_WIDTHS.DESKTOP}) {
    margin-left: 75px;
  }
  @media (max-width: ${SCREEN_WIDTHS.LAPTOP}) {
    max-width: 892px;
    margin: 0 auto;
  }
  @media (max-width: ${SCREEN_WIDTHS.TABLET_PORTRAIT}) {
    max-width: 634px;
    margin-left: 72px;
  }
`;

const StoreLayout = ({ children }: { children?: React.ReactNode }) => {
  const { employee, company }: { employee: Employee; company: Company } =
    useContext(CurrentUserContext);

  const {
    alegeus_pretax_accounts,
    pretax_accounts: forma_pretax_accounts,
    new_pretax_experience,
    is_loading_pretax,
  } = useContext(PretaxContext);

  const feature_flag = useContext(FeatureFlagContext);

  const is_alegeus_migrated_customer: boolean = feature_flag.flag(
    FEATURE_FLAGS.ALEGEUS_MIGRATED_CUSTOMER,
    false
  );

  const { employee_wallet_id } = useParams() as {
    employee_wallet_id: string;
  };
  const handleError = useErrorHandler();

  const {
    settings: { country, is_posttax_enabled },
    employee_wallets,
  } = employee;

  const is_forma_pretax_exp = useMemo(
    () => is_alegeus_migrated_customer && new_pretax_experience,
    [is_alegeus_migrated_customer, new_pretax_experience]
  );

  const pretax_accounts = useMemo(
    () =>
      is_forma_pretax_exp ? forma_pretax_accounts : alegeus_pretax_accounts,
    [is_forma_pretax_exp, alegeus_pretax_accounts, forma_pretax_accounts]
  );

  const active_employee_wallets = useMemo(() => {
    if (!allowMarketplace(employee, company)) {
      return [];
    }
    const { company_wallet_configurations } = company;

    return getActiveEmployeeWallets({
      company_wallet_configurations,
      employer_sponsored_wallets: employee_wallets,
      store_enabled: true,
    });
  }, [company, employee_wallets, employee]);

  const results = useQueryCategoriesWithProducts({
    employee_wallets: active_employee_wallets,
    country,
    onError: (error: Error) => handleError(error),
  });
  const categories_with_vendors = allowMarketplace(employee, company)
    ? results.data ?? []
    : [];

  //We only want to show one pre_tax account, either FSA, HSA, or LPFSA.
  const active_pretax_accounts = is_forma_pretax_exp
    ? filterActiveFormaPretaxAccounts({
        pretax_accounts: pretax_accounts as Array<PretaxWallet>,
      })
    : filterActiveAlegeusPretaxAccountsWithFsaType({
        pretax_accounts: pretax_accounts as Array<PretaxAccount>,
      });

  const filtered_wallet = employee_wallet_id
    ? employee_wallets.find(({ id }: $TSFixMe) => id === employee_wallet_id)
    : selectDefaultWallet({ employee_wallets });

  if (
    !is_posttax_enabled &&
    !allowMarketplace(employee, company) &&
    !is_loading_pretax &&
    active_pretax_accounts.length === 0
  ) {
    return <Navigate to={AccountRoutes.list()} replace />;
  }

  const fsa_and_lpf = is_forma_pretax_exp
    ? (active_pretax_accounts as PretaxWallet[]).find(
        ({ wallet_type_id }) =>
          wallet_type_id === 'PRETAX_ACCOUNT_V2_TYPES.LPFSA'
      )
    : (active_pretax_accounts as PretaxAccount[]).find(
        ({ account_type }) =>
          account_type.toLowerCase() === FSA_STORE_PRETAX_ACCOUNT_TYPES.LPF ||
          account_type.toLowerCase() === FSA_STORE_PRETAX_ACCOUNT_TYPES.LPFSA ||
          account_type.toLowerCase() === FSA_STORE_PRETAX_ACCOUNT_TYPES.LFS
      );

  return (
    <Container>
      <CategoryFilters
        employee_wallets={active_employee_wallets}
        filtered_wallet={filtered_wallet}
        categories_with_vendors={categories_with_vendors}
        pretax_accounts={active_pretax_accounts}
        has_fsa_and_lpf={Boolean(fsa_and_lpf)}
        is_forma_pretax={is_forma_pretax_exp}
      />
      {children ?? <Outlet />}
    </Container>
  );
};

export default StoreLayout;
